import { useSession } from 'next-auth/react';
import GameState from '../../src/enum/gamestate';
import { GameSocketMessageEventTypes, GameSocketNotifyMessageTypes, IGameSocketNotifyMessage } from '../../src/models/game/socket';
import { ApiGame, GameId } from '../../src/types/apigame';
import { usePageFocus } from '../../components/global/pagefocus';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSocket } from './socket';
import ActiveGamesClient from '../../src/util/activegames';
import { GameStateChanges } from '../../src/types/socket';
import { GetGamesForUserType } from '../../src/models/socket/game';
import type { ActiveGamesLoaderValue, ServicePropsFromServer } from '../../src/server/models/game/services/types';
import { OrderedMap } from '@js-sdsl/ordered-map';
import Game from '../../src/models/game';
import { GamePrivacy } from '../../src/enum/game';
import { ONE_MINUTE } from '../../src/util/relativetime';

export type ActiveGamesProviderValue = {
    activeGames: ApiGame[];
    completedGames: ApiGame[];
    openGames: ApiGame[];
    liveGames: ApiGame[];
    updateGames: (games: ApiGame[]) => void;
    markGameAsSeen: (gameId: GameId, lastSeenActionId?: number, lastSeenChatActionId?: number) => void;
}

const ActiveGamesProviderContext = createContext<ActiveGamesProviderValue>({
    activeGames: [],
    completedGames: [],
    openGames: [],
    liveGames: [],
    updateGames: () => { },
    markGameAsSeen: () => { },
});

const LIVE_GAMES_REFRESH_MS = ONE_MINUTE;
const FULL_GAMES_REFRESH_STALE_MS = 5 * ONE_MINUTE;
const FULL_GAMES_REFRESH_STALE_CHECK_MS = ONE_MINUTE;
const FULL_GAMES_REFRESH_RETRY_MS = ONE_MINUTE;

type Props = {
    children: ReactNode;
} & ServicePropsFromServer;

function initOrderedMap(
    loader: ActiveGamesLoaderValue,
): OrderedMap<GameId, ApiGame> {
    const map = new OrderedMap<GameId, ApiGame>(
        [],
        (a, b) => b - a, // sort by id descending
    );

    if (!loader) {
        return map;
    }

    const values = Object.values(loader);
    for (const gamesList of values) {
        for (const game of gamesList) {
            map.setElement(game.id, game);
        }
    }

    return map;
}

export function ActiveGamesProvider({ fromServer, children }: Props) {
    const isPageFocused = usePageFocus();
    const { socket } = useSocket();
    const [numUpdates, setNumUpdates] = useState<number>(0);
    const [games] = useState<OrderedMap<GameId, ApiGame>>(
        initOrderedMap(fromServer?.activeGamesLoader),
    );
    // The server-rendered data is fresh at hydration; initialize the timestamp after mount to keep render pure.
    const lastFullRefreshAtRef = useRef<number>(0);
    const lastFullRefreshAttemptAtRef = useRef<number>(0);
    const isFullRefreshInFlightRef = useRef<boolean>(false);

    const { data: session, status } = useSession();
    const userId = status === 'authenticated' ? session.user.id : null;

    const updateGames = useCallback(
        (gamesToUpdate: ApiGame[]) => {
            if (!gamesToUpdate) {
                return;
            }

            for (const game of gamesToUpdate) {
                games.setElement(game.id, game);
            }

            setNumUpdates((n) => n + 1);
        },
        [games, setNumUpdates],
    );

    const applyGamesWithLiveStatus = useCallback(
        (gamesToApply: ApiGame[], liveGames: ApiGame[]): void => {
            const liveGameIds = new Set(liveGames.map((game) => game.id));
            let didUpdateGame = false;

            for (const [gameId, game] of games) {
                if (game.isLive && !liveGameIds.has(gameId)) {
                    games.setElement(
                        gameId,
                        {
                            ...game,
                            isLive: false,
                        },
                    );
                    didUpdateGame = true;
                }
            }

            for (const game of gamesToApply) {
                games.setElement(
                    game.id,
                    {
                        ...game,
                        isLive: liveGameIds.has(game.id),
                    },
                );
                didUpdateGame = true;
            }

            if (didUpdateGame) {
                setNumUpdates((n) => n + 1);
            }
        },
        [games, setNumUpdates],
    );

    const updateLiveGames = useCallback(
        (liveGames: ApiGame[] | null): void => {
            if (!liveGames) {
                return;
            }

            applyGamesWithLiveStatus(liveGames, liveGames);
        },
        [applyGamesWithLiveStatus],
    );

    const applyGamesLists = useCallback(
        (gamesLists: ActiveGamesLoaderValue | null): boolean => {
            if (!gamesLists) {
                return false;
            }

            const hasLiveList = Object.prototype.hasOwnProperty.call(gamesLists, GetGamesForUserType.Live);
            const liveGames = hasLiveList ? gamesLists[GetGamesForUserType.Live] : null;
            const gamesToApply = Object.values(gamesLists).flat();

            if (hasLiveList) {
                applyGamesWithLiveStatus(gamesToApply, liveGames ?? []);
            } else {
                updateGames(gamesToApply);
            }

            return true;
        },
        [applyGamesWithLiveStatus, updateGames],
    );

    const client = useMemo(
        (): ActiveGamesClient => new ActiveGamesClient(socket, userId),
        [socket, userId],
    );

    const fetchActiveGames = useCallback(
        async () => {
            const newGames = await client.getActiveGames();
            updateGames(newGames);
        },
        [client, updateGames],
    );

    const fetchOpenGames = useCallback(
        async () => {
            const newGames = await client.getOpenGames();
            updateGames(newGames);
        },
        [client, updateGames],
    );

    const fetchLiveGames = useCallback(
        async () => {
            const newGames = await client.getLiveGames();
            updateLiveGames(newGames);
        },
        [client, updateLiveGames],
    );

    const fetchCompletedGames = useCallback(
        async () => {
            const newGames = await client.getCompletedGames();
            updateGames(newGames);
        },
        [client, updateGames],
    );

    const fetchGamesLists = useCallback(
        async (): Promise<void> => {
            if (isFullRefreshInFlightRef.current) {
                return;
            }

            isFullRefreshInFlightRef.current = true;

            try {
                const gamesLists = await client.getGamesLists();
                const didApplyGamesLists = applyGamesLists(gamesLists);

                if (didApplyGamesLists) {
                    lastFullRefreshAtRef.current = Date.now();
                }
            } catch (err) {
                console.error('ActiveGamesProvider: error refreshing games lists', err);
            } finally {
                lastFullRefreshAttemptAtRef.current = Date.now();
                isFullRefreshInFlightRef.current = false;
            }
        },
        [applyGamesLists, client],
    );

    const fetchGamesListsIfStale = useCallback(
        async (): Promise<void> => {
            if (!isPageFocused) {
                return;
            }

            if (lastFullRefreshAtRef.current === 0) {
                lastFullRefreshAtRef.current = Date.now();
                return;
            }

            const msSinceRefresh = Date.now() - lastFullRefreshAtRef.current;
            if (msSinceRefresh < FULL_GAMES_REFRESH_STALE_MS) {
                return;
            }

            const msSinceAttempt = Date.now() - lastFullRefreshAttemptAtRef.current;
            if (msSinceAttempt < FULL_GAMES_REFRESH_RETRY_MS) {
                return;
            }

            await fetchGamesLists();
        },
        [fetchGamesLists, isPageFocused],
    );

    const initGames = useCallback(
        async (): Promise<void> => {
            if (!isPageFocused) {
                return;
            }

            const loadPromises: Promise<void>[] = [];

            if (!fromServer?.activeGamesLoader?.[GetGamesForUserType.Active]?.length) {
                loadPromises.push(fetchActiveGames());
            }

            if (!fromServer?.activeGamesLoader?.[GetGamesForUserType.Open]?.length) {
                loadPromises.push(fetchOpenGames());
            }

            if (!fromServer?.activeGamesLoader?.[GetGamesForUserType.Completed]?.length) {
                loadPromises.push(fetchCompletedGames());
            }

            try {
                await Promise.allSettled(loadPromises);
            } catch (err) {
                console.error('ActiveGamesProvider: error loading games', err);
            }
        },
        [fromServer, isPageFocused, fetchActiveGames, fetchOpenGames, fetchCompletedGames],
    );

    const fetchAndUpdateGame = useCallback(
        async (gameId: GameId): Promise<void> => {
            const game = await client.getGame(gameId);

            if (!game) {
                return;
            }

            updateGames([game]);
        },
        [client, updateGames],
    );

    useEffect(
        () => {
            if (!socket) {
                return;
            }

            // Avoid a synchronous state update inside the effect body while still loading immediately after mount.
            queueMicrotask(initGames);

            socket.on('connect', initGames);
            socket.on('connect', fetchGamesListsIfStale);
            return () => {
                socket.off('connect', initGames);
                socket.off('connect', fetchGamesListsIfStale);
            }
        },
        [socket, initGames, fetchGamesListsIfStale],
    );

    useEffect(
        () => {
            if (!socket || !isPageFocused) {
                return;
            }

            queueMicrotask(fetchGamesListsIfStale);

            const intervalId = window.setInterval(
                fetchGamesListsIfStale,
                FULL_GAMES_REFRESH_STALE_CHECK_MS,
            );

            return () => {
                window.clearInterval(intervalId);
            };
        },
        [socket, isPageFocused, fetchGamesListsIfStale],
    );

    useEffect(
        () => {
            if (!socket || !isPageFocused) {
                return;
            }

            queueMicrotask(fetchLiveGames);

            const intervalId = window.setInterval(
                fetchLiveGames,
                LIVE_GAMES_REFRESH_MS,
            );

            return () => {
                window.clearInterval(intervalId);
            };
        },
        [socket, isPageFocused, fetchLiveGames],
    );

    useEffect(
        () => {
            if (!socket) {
                return;
            }

            const onNotifyGameChanged = (data: GameStateChanges) => {
                fetchAndUpdateGame(data.gameId);
            };

            const onActiveGameChange = (message: IGameSocketNotifyMessage) => {
                switch (message.type) {
                    case GameSocketNotifyMessageTypes.Invited:
                    case GameSocketNotifyMessageTypes.Turn:
                    case GameSocketNotifyMessageTypes.DeclinedInvite:
                    case GameSocketNotifyMessageTypes.GameStarted:
                    case GameSocketNotifyMessageTypes.Joined:
                    case GameSocketNotifyMessageTypes.TurnEnded:
                    case GameSocketNotifyMessageTypes.NotifyGameChanged: {
                        const gameId = message.gameId;
                        fetchAndUpdateGame(gameId);

                        break;
                    }
                    case GameSocketNotifyMessageTypes.Open: {
                        const gameId = message.gameId;
                        fetchAndUpdateGame(gameId);

                        break;
                    }

                    case GameSocketNotifyMessageTypes.GameIsLive: {
                        const gameId = message.gameId;
                        fetchAndUpdateGame(gameId);

                        break;
                    }

                    case GameSocketNotifyMessageTypes.GameIsNoLongerLive: {
                        const gameId = message.gameId;
                        fetchAndUpdateGame(gameId);

                        break;
                    }
                }
            };

            socket.on(GameSocketMessageEventTypes.Notify, onActiveGameChange);
            socket.on('notifyGameChanged', onNotifyGameChanged);

            return () => {
                socket.off(GameSocketMessageEventTypes.Notify, onActiveGameChange);
                socket.off('notifyGameChanged', onNotifyGameChanged);
            };
        },
        [socket, fetchAndUpdateGame],
    );

    const markGameAsSeen = useCallback(
        (gameId: GameId, lastSeenActionId?: number, lastSeenChatActionId?: number): void => {
            const game = games.getElementByKey(gameId);
            if (!game) {
                return;
            }

            const newGame = { ...game };

            if (lastSeenActionId != null) {
                newGame.lastSeenAction = lastSeenActionId;
            }

            if (lastSeenChatActionId != null) {
                newGame.lastSeenChatAction = lastSeenChatActionId;
            }

            updateGames([newGame]);
        },
        [games, updateGames],
    );

    useEffect(
        () => {
            if (typeof window === 'undefined') {
                return;
            }

            window['spqr'] = {
                ...window['spqr'],
                games: games,
            };
        },
        [games, numUpdates],
    );

    const value = useMemo(
        (): ActiveGamesProviderValue => {
            // no-op to make sure this variable is a dependency.
            // it would be wasteful to reallocate the map after
            // every fetch/update, so instead we just use this
            // variable to indicate an update has occurred.
            numUpdates;

            const activeGames: ApiGame[] = [];
            const openGames: ApiGame[] = [];
            const liveGames: ApiGame[] = [];
            const completedGames: ApiGame[] = [];

            for (const [_, game] of games) {
                const g = new Game(game);

                const isInvited = g.isUserInvited(userId);
                const seatObject = g.getSeatObjectByUserId(userId);

                if (seatObject) {
                    if (game.state === GameState.Complete) {
                        completedGames.push(game);
                    }

                    if (
                        game.state !== GameState.Complete
                        || g.isGameOverAndUnseen(userId)
                    ) {
                        activeGames.push(game);
                    }
                } else if (isInvited) {
                    if (game.state === GameState.Created) {
                        activeGames.push(game);
                    }
                }

                if (
                    game.privacy === GamePrivacy.AnyoneCanJoin
                    && game.seats.length < game.maxSeats
                    && game.state === GameState.Created
                ) {
                    openGames.push(game);
                }

                if (
                    game.isLive
                    && game.state !== GameState.Created
                    && game.state !== GameState.Complete
                ) {
                    liveGames.push(game);
                }
            }

            return { activeGames, openGames, liveGames, completedGames, updateGames, markGameAsSeen };
        },
        [games, numUpdates, userId, updateGames, markGameAsSeen],
    );

    return (
        <ActiveGamesProviderContext.Provider value={value}>
            {children}
        </ActiveGamesProviderContext.Provider>
    );
}

export function useActiveGamesProvider() {
    return useContext(ActiveGamesProviderContext);
}
